import { Group, Menu, Text, Tooltip } from '@mantine/core';
import {
  IconArrowBackUp,
  IconCheckbox,
  IconDotsVertical,
  IconEye,
  IconFileUpload,
  IconTrashX,
} from '@tabler/icons-react';
import { useApi } from 'api/api-context';
import ActionIcon from 'components/ActionIcon';
import Button from 'components/Button';
import { useConfirmWithMessage } from 'components/modals/confirm-with-message/ConfirmWithMessageProvider';
import { PriceOfferRow, PriceOfferStatusChangingAction } from 'components/tables/data/price-offer/types';
import { useResponsiveDesign } from 'hooks/use-responsive-design';
import { noop } from 'lodash';
import { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { PRICE_OFFER_PAGE_PATH, PROVIDE_PRICE_OFFER_PAGE_PATH } from 'routes/paths';

type ActionName = 'provide' | 'evaluate' | 'return' | 'view' | 'delete';
type ActionKind = 'button' | 'action-icon' | 'menu';

export const PRICE_OFFER_PRIMARY_ACTION_WIDTH = 160;
export const PRICE_OFFER_ACTIONS_COLUMN_WIDTH = PRICE_OFFER_PRIMARY_ACTION_WIDTH + 40 + 12 + 24;

interface PriceOfferActionsProps {
  priceOffer: PriceOfferRow;
  compactOnTablet?: boolean;
  keepPrimaryActionOnMobile?: boolean;
  onChanged?: (actionName?: PriceOfferStatusChangingAction, priceOffer?: PriceOfferRow) => void;
}

/**
 * Renders contextual actions for a price offer.
 */
export default function PriceOfferActions({
  priceOffer,
  compactOnTablet = false,
  keepPrimaryActionOnMobile = false,
  onChanged = noop,
}: PriceOfferActionsProps) {
  const { getAction, userId } = useApi();
  const { confirmWithMessage } = useConfirmWithMessage();
  const { pathname } = useLocation();
  const { isMobile, tableActionMode } = useResponsiveDesign();
  const isCompact = compactOnTablet && tableActionMode !== 'desktop';
  const menuOnly = compactOnTablet && tableActionMode === 'mobile' && !keepPrimaryActionOnMobile;

  /**
   * Confirms and deletes the price offer.
   */
  const deletePriceOffer = () =>
    confirmWithMessage({
      title: 'Zmazať cenovú ponuku',
      message: 'Naozaj chcete zmazať túto cenovú ponuku?',
      reasonLabel: 'Poznámka',
      primaryButtonText: 'Áno',
      secondaryButtonText: 'Nie',
      onConfirmWithMessage: async (note?: string) => {
        const action = getAction('PriceOfferDelete');
        await action({
          parameters: { priceOfferId: String(priceOffer.priceOfferId) },
          payload: { note },
        });
        onChanged('delete', priceOffer);
      },
    });

  /**
   * Opens the shared evaluation modal for a filled price offer.
   */
  const evaluatePriceOffer = () =>
    confirmWithMessage({
      title: 'Vyhodnotiť cenovú ponuku',
      message: 'Vyberte, či je cenová ponuka vyhovujúca alebo nevyhovujúca.',
      reasonLabel: 'Poznámka',
      selectLabel: 'Vyhodnotenie',
      selectPlaceholder: 'Vyberte vyhodnotenie',
      selectOptions: [
        { value: 'acceptable', label: 'Vyhovujúca' },
        { value: 'unacceptable', label: 'Nevyhovujúca' },
      ],
      requiredSelect: true,
      onConfirmWithMessage: async (note?: string, decision?: string) => {
        const action = getAction('PriceOfferClose');
        await action({
          parameters: { priceOfferId: String(priceOffer.priceOfferId) },
          payload: { decision: decision ?? 'acceptable', note },
        });
        onChanged(decision === 'unacceptable' ? 'unacceptable' : 'acceptable', priceOffer);
      },
    });

  /**
   * Confirms and returns the filled price offer to the assignee.
   */
  const returnPriceOffer = () =>
    confirmWithMessage({
      title: 'Vrátiť cenovú ponuku',
      message: 'Naozaj chcete vrátiť túto cenovú ponuku na dopracovanie?',
      requiredReason: true,
      onConfirmWithMessage: async (reason?: string) => {
        const action = getAction('PriceOfferReturn');
        await action({
          parameters: { priceOfferId: String(priceOffer.priceOfferId) },
          payload: { reason: reason ?? '' },
        });
        onChanged('return', priceOffer);
      },
    });

  const actions = useMemo(() => {
    const nextActions: ActionName[] = [];

    const priceOfferId = String(priceOffer.priceOfferId);
    const isOnDetailPage = pathname === PRICE_OFFER_PAGE_PATH.insert({ priceOfferId });
    const isAssignedToCurrentUser = priceOffer.assignedTo.userId === userId;

    if (priceOffer.actionPermissions.canProvide && isAssignedToCurrentUser && priceOffer.status.slug === 'created') {
      nextActions.push('provide');
    }

    if (priceOffer.actionPermissions.canClose) {
      nextActions.push('evaluate');
    }

    if (priceOffer.actionPermissions.canReturn) {
      nextActions.push('return');
    }

    if (!isOnDetailPage) {
      nextActions.push('view');
    }

    if (
      priceOffer.actionPermissions.canDelete &&
      !['acceptable', 'unacceptable', 'deleted'].includes(priceOffer.status.slug)
    ) {
      nextActions.push('delete');
    }

    return nextActions;
  }, [pathname, priceOffer, userId]);

  const primaryAction = useMemo(() => (menuOnly ? null : actions[0] || null), [actions, menuOnly]);
  const menuActions = useMemo(() => (menuOnly ? actions : actions.slice(1)), [actions, menuOnly]);

  /**
   * Renders an action either as the primary button or as a menu item.
   */
  const renderAction = (actionName: ActionName, kind: ActionKind) => {
    const isMenu = kind === 'menu';
    const priceOfferId = String(priceOffer.priceOfferId);
    if (actionName === 'provide') {
      const to = PROVIDE_PRICE_OFFER_PAGE_PATH.insert({ priceOfferId });
      const icon = <IconFileUpload stroke={1.5} size={isMenu ? 22 : 24} />;
      const text = 'Vypracovať';

      return isMenu ? (
        <Menu.Item key={actionName} component={Link} to={to} icon={icon}>
          {text}
        </Menu.Item>
      ) : kind === 'action-icon' ? (
        <Tooltip key={actionName} label={text} events={{ hover: true, focus: true, touch: true }}>
          <ActionIcon variant="primary" size="md" component={Link} to={to} aria-label={text}>
            <IconFileUpload stroke={1.5} size={20} />
          </ActionIcon>
        </Tooltip>
      ) : (
        <Button
          key={actionName}
          component={Link}
          to={to}
          size="md"
          leftIcon={icon}
          w={PRICE_OFFER_PRIMARY_ACTION_WIDTH}
        >
          {text}
        </Button>
      );
    }

    if (actionName === 'evaluate') {
      const icon = <IconCheckbox stroke={1.5} size={isMenu ? 22 : 24} />;
      const text = 'Vyhodnotiť';

      return isMenu ? (
        <Menu.Item key={actionName} onClick={evaluatePriceOffer} icon={icon}>
          {text}
        </Menu.Item>
      ) : kind === 'action-icon' ? (
        <Tooltip key={actionName} label={text} events={{ hover: true, focus: true, touch: true }}>
          <ActionIcon variant="primary" size="md" onClick={evaluatePriceOffer} aria-label={text}>
            <IconCheckbox stroke={1.5} size={20} />
          </ActionIcon>
        </Tooltip>
      ) : (
        <Button
          key={actionName}
          size="md"
          leftIcon={icon}
          onClick={evaluatePriceOffer}
          w={PRICE_OFFER_PRIMARY_ACTION_WIDTH}
        >
          {text}
        </Button>
      );
    }

    if (actionName === 'view') {
      const to = PRICE_OFFER_PAGE_PATH.insert({ priceOfferId });
      const icon = <IconEye stroke={1.5} size={isMenu ? 22 : 24} />;
      const text = 'Detail';

      return isMenu ? (
        <Menu.Item key={actionName} component={Link} to={to} icon={icon}>
          {text}
        </Menu.Item>
      ) : kind === 'action-icon' ? (
        <Tooltip key={actionName} label={text} events={{ hover: true, focus: true, touch: true }}>
          <ActionIcon variant="secondary" size="md" component={Link} to={to} aria-label={text}>
            <IconEye stroke={1.5} size={20} />
          </ActionIcon>
        </Tooltip>
      ) : (
        <Button
          key={actionName}
          component={Link}
          to={to}
          size="md"
          variant="secondary"
          leftIcon={icon}
          w={PRICE_OFFER_PRIMARY_ACTION_WIDTH}
        >
          {text}
        </Button>
      );
    }

    if (actionName === 'return') {
      const icon = <IconArrowBackUp stroke={1.5} size={isMenu ? 22 : 24} />;
      const text = 'Vrátiť';

      return isMenu ? (
        <Menu.Item key={actionName} color="gray.8" onClick={returnPriceOffer} icon={icon}>
          {text}
        </Menu.Item>
      ) : kind === 'action-icon' ? (
        <Tooltip key={actionName} label={text} events={{ hover: true, focus: true, touch: true }}>
          <ActionIcon variant="secondary" size="md" onClick={returnPriceOffer} aria-label={text}>
            <IconArrowBackUp stroke={1.5} size={20} />
          </ActionIcon>
        </Tooltip>
      ) : (
        <Button
          key={actionName}
          size="md"
          variant="secondary"
          leftIcon={icon}
          onClick={returnPriceOffer}
          w={PRICE_OFFER_PRIMARY_ACTION_WIDTH}
        >
          {text}
        </Button>
      );
    }

    const icon = <IconTrashX stroke={1.5} size={isMenu ? 22 : 24} />;
    const text = 'Zmazať';

    return isMenu ? (
      <Menu.Item key={actionName} color="red.8" onClick={deletePriceOffer} icon={icon}>
        {text}
      </Menu.Item>
    ) : kind === 'action-icon' ? (
      <Tooltip key={actionName} label={text} events={{ hover: true, focus: true, touch: true }}>
        <ActionIcon variant="danger-secondary" size="md" onClick={deletePriceOffer} aria-label={text}>
          <IconTrashX stroke={1.5} size={20} />
        </ActionIcon>
      </Tooltip>
    ) : (
      <Button
        key={actionName}
        size="md"
        variant="danger-secondary"
        leftIcon={icon}
        onClick={deletePriceOffer}
        w={PRICE_OFFER_PRIMARY_ACTION_WIDTH}
      >
        {text}
      </Button>
    );
  };

  return (
    <Group noWrap spacing={12}>
      {primaryAction && renderAction(primaryAction, isCompact ? 'action-icon' : 'button')}
      {menuActions.length > 0 && (
        <Menu shadow="md" width={220} position="bottom-end" withinPortal>
          <Menu.Target>
            <ActionIcon variant="tertiary" size={isMobile ? 'sm' : 'md'}>
              <IconDotsVertical stroke="1.5" />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Label>
              <Text maw={160} truncate>
                {priceOffer.device.deviceName}
              </Text>
            </Menu.Label>
            {menuActions.map((actionName) => renderAction(actionName, 'menu'))}
          </Menu.Dropdown>
        </Menu>
      )}
    </Group>
  );
}
